import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { IconButton } from "@/sprout/icon_button";
import CloseIcon from "@/sprout/icons/close";
import CheckMarkCircleIcon from "@sprout/icons/check_mark_circle";
import UploadIcon from "@sprout/icons/upload";
import LoadingSpinner from "@/sprout/loading_spinner";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import { Body } from "@sprout/typography";
import { Stack } from "@sprout/layout";
import { validateResume } from "@components/quick_apply/util";
import { useShowFlash } from "@sprout/flash";
import useDevice from "@/hooks/use_device";
import type { FileState } from "@/queries/use_profile";
import quickApplyBannerDesktop from "@images/email_campaign/quick_apply_profile_email_desktop_banner.png";
import quickApplyBannerMobile from "@images/email_campaign/quick_apply_profile_email_mobile_banner.png";
import { cn } from "@/lib/utils";

type Step = "prompt" | "uploading" | "complete";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DashboardUploadResumeModal = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "dashboard_upload_resume_modal" });
  const { t: tResume } = useTranslation("user");
  const showFlash = useShowFlash();
  const queryClient = useQueryClient();
  const { isMobile } = useDevice();
  const buttonSize = isMobile ? "medium" : "large";

  const [step, setStep] = useState<Step>("prompt");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = useCallback(() => {
    if (step === "uploading" && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStep("prompt");
    setDragOver(false);
    setUploadError(null);
    onOpenChange(false);
  }, [step, onOpenChange]);

  const uploadFile = useCallback(
    async (file: File) => {
      const resumeUpload: FileState = {
        file,
        filename: file.name,
        url: URL.createObjectURL(file),
        content_type: file.type,
      };

      const resumeError = validateResume(resumeUpload);
      if (resumeError) {
        const errorKey =
          resumeError === "resume.invalidFileType" ? "file_invalid_type" : "file_too_large";
        setUploadError(`${file.name}: ${t(errorKey)}`);
        return;
      }

      setUploadError(null);
      setStep("uploading");

      const formData = new FormData();
      formData.append("resume", file);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        await axios.post("/users/resume", formData, {
          signal: controller.signal,
        });
        await queryClient.invalidateQueries({ queryKey: ["profile"] });
        setStep("complete");
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }
        showFlash(tResume("resume.error_flash"), "error");
        setStep("prompt");
      } finally {
        abortControllerRef.current = null;
      }
    },
    [showFlash, tResume, queryClient],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadFile(file);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [uploadFile],
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className={cn(
          "max-w-[333px] p-6",
          step === "prompt" ? "gap-2 sm:gap-4 sm:max-w-[600px]" : "!flex flex-col sm:max-w-[450px]",
          step === "uploading" && "h-72 gap-2 sm:gap-6",
          step === "complete" && "gap-0",
        )}
      >
        <DialogHeader className="text-left px-2 pb-2 sm:p-0">
          <DialogTitle>
            <div className="text-[18px] leading-[24px]">
              {step === "prompt" && t("title")}
              {step === "uploading" && t("uploading_title")}
              {step === "complete" && t("complete_title")}
            </div>
          </DialogTitle>
          {step !== "uploading" && !(isMobile && step === "complete") && (
            <IconButton icon={CloseIcon} size="md" label="Close" onClick={handleClose} />
          )}
        </DialogHeader>

        <DialogDescription>
          <div className={cn(step === "uploading" && "flex-1 flex flex-col")}>
            {step === "prompt" && (
              <Stack gap="12px">
                <img
                  src={isMobile ? quickApplyBannerMobile : quickApplyBannerDesktop}
                  alt={t("title")}
                  className="w-full rounded-lg"
                />
                <div className="py-[12px]">
                  <Body>{t("description")}</Body>
                </div>
                <div
                  className={cn(
                    "flex flex-col items-center justify-center w-full rounded-[5px] gap-1",
                    "p-4 sm:px-[26px] sm:py-[26px]",
                    "cursor-pointer transition-colors border border-dashed",
                    dragOver
                      ? "bg-[var(--green-50)] border-[var(--green-500)]"
                      : "border-[var(--gray-700)]",
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="flex items-center justify-center gap-3">
                    <UploadIcon size="sm" />
                    <Body>
                      <button
                        type="button"
                        className="underline text-[var(--green-700)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        {t("browse")}
                      </button>{" "}
                      {t("drop_hint")}
                    </Body>
                  </div>
                  <Body metadata className="text-center">
                    {t("file_types")}
                  </Body>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.rtf"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {uploadError && (
                  <p className="helper-text helper-text--error !ml-0" aria-live="polite">
                    {uploadError}
                  </p>
                )}
              </Stack>
            )}

            {step === "uploading" && (
              <div className="flex items-center justify-center flex-1">
                <LoadingSpinner size="large" color="green" />
              </div>
            )}

            {step === "complete" && (
              <Stack gap="16px" className="mt-[28px]">
                <div className="flex items-center gap-2 bg-[var(--green-50)] w-full rounded-[5px] py-3 px-4">
                  <CheckMarkCircleIcon size="md" viewBox="0 0 16 16" color="green" />
                  <Body>{t("complete_message")}</Body>
                </div>
                <Body>{t("complete_description")}</Body>
              </Stack>
            )}
          </div>
        </DialogDescription>

        {step === "uploading" && (
          <DialogFooter className="flex-row justify-end mt-auto">
            <SecondaryButton size={buttonSize} onClick={handleClose}>
              {t("cancel")}
            </SecondaryButton>
          </DialogFooter>
        )}

        {step === "complete" && (
          <DialogFooter className="flex-row justify-end items-center mt-[36px]">
            <SecondaryButton size={buttonSize} onClick={handleClose}>
              {t("maybe_later")}
            </SecondaryButton>
            <PrimaryButton
              size={buttonSize}
              onClick={() => (window.location.href = "/quick_apply?from_resume_upload=true")}
            >
              {t("review_profile")}
            </PrimaryButton>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DashboardUploadResumeModal;
